import { observer, useViewModel } from '@descomplica/microfrontend-react';
import React, { ClipboardEvent, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import styled, { css } from 'styled-components';
import { v4 as uuid } from 'uuid';

import { importSheet, useOutsideClick, validateEmail } from '@/shared/application/utils';

import { CreateInvitationViewModel } from '../CreateInvitation.vm';

const ImportButton = styled.button`
  font-family: 'Aprova Nova';
  border: none;
  cursor: pointer;
  position: relative;
  text-align: center;
  overflow: hidden;
  box-sizing: border-box;
  font-weight: 700;
  height: 40px;
  border-radius: 500px;
  padding: 0px 24px;
  background-color: rgb(0, 248, 143);
  color: rgb(25, 26, 26);

  &:hover {
    background-color: rgb(0, 249, 157);
  }
`;

const ExampleImportInvitationDownload = styled.a`
  font-size: 14px;
  cursor: pointer;
`;

const InputContainer = styled.div<{ hasTags: boolean; focused: boolean }>`
  height: 206px;
  overflow: auto;

  width: 100%;
  display: flex;
  flex-wrap: wrap;
  align-items: end;
  align-content: start;

  border: 1px solid #caccce;
  border-radius: 8px;

  font-size: 16px;

  padding: 16px 16px 16px 24px;

  box-sizing: border-box;
  box-shadow: none;
  outline: none;

  gap: 10px;
  position: relative;

  ${({ hasTags }) =>
    hasTags &&
    css`
      input {
        &::-webkit-input-placeholder {
          color: transparent;
        }
      }
    `};
`;

const LabelBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const LabelStyled = styled.label`
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  color: black;
  font-weight: bold;
`;

const TagItem = styled.section`
  background-color: #ffe3ac;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2px 12px;
  gap: 4px;
  border-radius: 13px;
  text-align: center;

  span {
    font-weight: normal;
    font-size: 16px;
    line-break: anywhere;
    text-align: start;
  }

  .close-button {
    cursor: pointer;
    color: black;
    font-weight: lighter;
  }
`;

const InputStyled = styled.input`
  width: 1%;
  flex-grow: 1;
  font-size: 16px;
  cursor: text;
  border: none;
  outline: 0;
  min-width: 50px;

  &::placeholder {
    color: #96999c;
  }

  &::-webkit-input-placeholder {
    transition: color 300ms ease;
  }

  &:not(:focus)::-webkit-input-placeholder {
    color: transparent;
  }
`;

const HeaderInput = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.25rem;
`;

const FileOptions = styled.div`
  font-size: 14px;

  display: flex;
  gap: 0.25rem;
  align-items: center;

  div:first-child {
    font-size: 14px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 300px;
    cursor: default;

    &:hover {
      max-width: fit-content;
    }
  }

  button {
    cursor: pointer;
    color: #161616;
    border: 0;
    background: none;

    &:hover:after {
      content: attr(data-tooltip);
      position: absolute;
      margin-left: 5px;
      margin-top: 10px;
      border: solid 1px #dedede;
      border-radius: 10px;
      background-color: white;
      padding: 5px;
      font-size: 12px;
      z-index: 10;
    }

    &:hover {
      color: black;
    }
  }
`;

export type Tag = {
  guid: string;
  name: string;
};

export type InputFileProps = {
  handleFileUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
};

const InputFile = ({ handleFileUpload }: InputFileProps) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    handleFileUpload(e);
  };

  const handleImportButton = () => {
    inputRef.current.click();
  };

  return (
    <>
      <input
        type="file"
        accept=".xls,.xlsx,.csv"
        ref={inputRef}
        onChange={onFileChange}
        style={{ display: 'none' }}
      />
      <ImportButton type="button" onClick={handleImportButton}>
        Importar
      </ImportButton>
    </>
  );
};

export type MultipleEmailInputProps = {
  label: string;
  placeholder?: string;
  values: Tag[];
  setValues: (tags: any) => void;
  onError?: (invalidEmails: any, validEmail: Tag[]) => void;
  ref?: any;
};

const MultipleEmailInput = observer(
  ({ label, placeholder, values, setValues, onError }: MultipleEmailInputProps) => {
    const vm = useViewModel(CreateInvitationViewModel);

    const inputElement = useRef(null);
    const wrapperElement = useRef(null);
    const [inputValue, setInputValue] = useState('');
    const [focused, setFocused] = useState(false);
    const [fileName, setFileName] = useState<string | null>();

    useOutsideClick(wrapperElement, () => setFocused(false));

    const handleInvalidEmails = (invalidEmails: string[], validEmails: Tag[]) => {
      onError(invalidEmails, validEmails);
    };

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
      vm.resetImport();
      setFileName(e.target.value.split(/(\\|\/)/g).pop());

      const sheet = (await importSheet(e)) as any[];
      const emailArray = sheet.map((data) => data['E-mail']);

      if (emailArray.length > 10000) emailArray.length = 10000;

      const invalidEmails = [];

      const emailsFormated = emailArray
        .filter((email) => {
          if (!validateEmail(email)) {
            invalidEmails.push(email);
            return;
          }

          return email;
        })
        .map((email) => {
          return {
            guid: uuid(),
            name: email,
          };
        });

      if (invalidEmails.length > 0) {
        handleInvalidEmails(invalidEmails, emailsFormated);
        e.target.value = '';

        return;
      }

      e.target.value = '';
      vm.emailsImported = emailsFormated;
      addMultiTags(emailsFormated);
    };

    const handleKeyDown = (e: any) => {
      const value = e.target.value;

      if (
        (e.key === 'Backspace' || e.key === 'Delete') &&
        values.length > 0 &&
        e.target.value === ''
      ) {
        const tagIndexForRemove = values.length - 1;
        removeTag(values[tagIndexForRemove]);
        return;
      }
      if (!value.trim()) {
        return;
      }
      if (e.key === ' ' || e.key === ',' || e.key === ';' || e.key === 'Enter') {
        if (!validateEmail(value)) {
          e.target.value = '';
          toast.error('Email Invalido');
          return;
        }

        addTag({ guid: uuid(), name: value });

        return;
      }
    };

    const onChangeHandler = (inputValue: string) => {
      if (
        inputValue.charAt(inputValue.length - 1) === ',' ||
        inputValue.charAt(inputValue.length - 1) === ';' ||
        inputValue.charAt(inputValue.length - 1) === ' '
      ) {
        return;
      }
      setInputValue(inputValue);
    };

    const onPasteHandler = (e: ClipboardEvent<HTMLInputElement>) => {
      const invalidEmails = [];

      e.clipboardData.items[0].getAsString((text) => {
        if (text.match(/[,;\s]/)) {
          const emailArray = text.split(/[,;\s]+/);

          const emailsFormated = emailArray
            .filter((email) => {
              if (!validateEmail(email)) {
                invalidEmails.push(email);
                return;
              }

              return email;
            })
            .map((email) => {
              return {
                guid: uuid(),
                name: email,
              };
            });

          if (invalidEmails.length > 0) {
            handleInvalidEmails(invalidEmails, emailsFormated);
            setInputValue('');
            return;
          }

          addMultiTags(emailsFormated);
        }
      });
    };

    const addTag = (newTag: Tag) => {
      const isTagDuplicated = values.some((tag) => newTag.name === tag.name);
      if (isTagDuplicated) {
        toast.error('Email duplicado');
        return;
      }

      setValues([...values, newTag]);

      setInputValue('');
    };

    const addMultiTags = (addedTags: Tag[]) => {
      const newTags = addedTags.filter((newTag) => !values.find((tag) => newTag.name === tag.name));
      const tagsNotDuplicated = [...new Map(newTags.map((tag) => [tag.name, tag])).values()];
      vm.emailsImported = tagsNotDuplicated;

      setValues([...tagsNotDuplicated]);
      setInputValue('');
    };

    const removeTag = (selectedTag: Tag) => {
      setValues([...values.filter((tag) => tag !== selectedTag)]);
      setFocused(true);
    };

    const handleDeleteFile = () => {
      vm.emailsImported = [];
      vm.form.values.emails = [];
      setFileName('');
    };

    return (
      <section ref={wrapperElement}>
        <HeaderInput>
          <LabelBox>
            <LabelStyled htmlFor={inputElement.current}>
              {label}
              {vm.emailsImported.length > 0 ? (
                ` - Foram importados ${vm.emailsImported.length} emails`
              ) : (
                <div>
                  <ExampleImportInvitationDownload
                    href={'static/invitationTemplate.xlsx'}
                    download={true}
                  >
                    Baixar Exemplo
                  </ExampleImportInvitationDownload>
                </div>
              )}
            </LabelStyled>
            {fileName && vm.emailsImported.length > 0 && (
              <FileOptions>
                <div>{fileName}</div>
                <button type="button" data-tooltip="Remover importação" onClick={handleDeleteFile}>
                  X
                </button>
              </FileOptions>
            )}
          </LabelBox>

          <InputFile handleFileUpload={handleFileUpload} />
        </HeaderInput>

        <InputContainer
          hasTags={!!values[0]}
          onClick={() => {
            if (!(vm.emailsImported.length > 0)) {
              inputElement.current.focus();
            }
          }}
          focused={focused}
        >
          {values.map((tag) => (
            <TagItem key={tag.guid} className="tag-item">
              <span>{tag.name}</span>
              {vm.emailsImported.length == 0 && (
                <span className="close-button" onClick={() => removeTag(tag)}>
                  &times;
                </span>
              )}
            </TagItem>
          ))}
          {vm.emailsImported.length == 0 && (
            <InputStyled
              ref={inputElement}
              onKeyDown={handleKeyDown}
              onChange={(e) => onChangeHandler(e.target.value)}
              value={inputValue}
              placeholder={placeholder || ''}
              autoComplete="off"
              onPaste={(e) => onPasteHandler(e)}
            />
          )}
        </InputContainer>
      </section>
    );
  },
);

export default MultipleEmailInput;
